import React from 'react';

// Type-only, so this does not create a runtime cycle with the module that renders these.
import type { ReportModalProps } from './reportModalRegistry';
import CommissioningCommLoopReport from 'src/screen/Protected/DeviceScreen/CommissioningCommLoop/CommissioningCommLoopReport';
import CommissioningCircuitReport from 'src/screen/Protected/DeviceScreen/CommissioningCircuit/CommissioningCircuitReport';
import CommissioningControllerReport from 'src/screen/Protected/DeviceScreen/CommissioningController/CommissioningControllerReport';
import CommissioningCablePreInsulationReport from 'src/screen/Protected/DeviceScreen/CommissioningCablePreInsulation/CommissioningCablePreInsulationReport';
import CommissioningCablePostInsulationReport from 'src/screen/Protected/DeviceScreen/CommissioningCablePostInsulation/CommissioningCablePostInsulationReport';
import HmsAuditReport from 'src/screen/Protected/DeviceScreen/HmsAudit/HmsAuditReport';
import EhtInsulationAuditReport from 'src/screen/Protected/DeviceScreen/EhtInsulationAudit/EhtInsulationAuditReport';
import PanelAuditReport from 'src/screen/Protected/DeviceScreen/PanelAudit/PanelAuditReport';
import EhtCircuitAuditReport from 'src/screen/Protected/DeviceScreen/EhtCircuitAudit/EhtCircuitAuditReport';

// What a list screen needs to offer a report type: its label, the device type its picker should
// offer, its PDF filename, and the modal that opens it. Deliberately not a copy of the API's
// CHECK_SHEET_TYPES — that config carries persistence concerns (tagPath, notesPath, hasExtras)
// this side has no use for, and these are rendering concerns the API has no use for. The type
// strings are the only overlap, and the packages build separately with no shared code.
//
// fileSlug is the stem of the downloaded PDF's filename. Declared per type rather than
// derived from the type string, so a type that isn't a commissioning report doesn't have to
// be named `commissioning-*` to get a sensible filename.
export type FieldReportCategory = 'commissioning' | 'audit';

export type FieldReportTypeDef = {
  value: string;
  labelKey: string;
  deviceType: string;
  fileSlug: string;
  Modal: React.ComponentType<ReportModalProps>;
};

// One array per screen. Membership is what puts a type on a screen — including which modals it
// can open — so there is no `category` field to keep in sync with the array a row sits in.
export const COMMISSIONING_TYPES: FieldReportTypeDef[] = [
  { value: 'commissioning-comm-loop-v2', labelKey: 'commissioning.commLoop', deviceType: 'commLoop', fileSlug: 'comm-loop-v2-commissioning-report', Modal: CommissioningCommLoopReport },
  { value: 'commissioning-circuit', labelKey: 'commissioning.circuit', deviceType: 'circuit', fileSlug: 'circuit-commissioning-report', Modal: CommissioningCircuitReport },
  { value: 'commissioning-controller', labelKey: 'commissioning.controller', deviceType: 'controller', fileSlug: 'controller-commissioning-report', Modal: CommissioningControllerReport },
  { value: 'commissioning-cable-pre-insulation', labelKey: 'commissioning.cablePreInsulation', deviceType: 'circuit', fileSlug: 'cable-pre-insulation-commissioning-report', Modal: CommissioningCablePreInsulationReport },
  { value: 'commissioning-cable-post-insulation', labelKey: 'commissioning.cablePostInsulation', deviceType: 'circuit', fileSlug: 'cable-post-insulation-commissioning-report', Modal: CommissioningCablePostInsulationReport },
];

export const AUDIT_TYPES: FieldReportTypeDef[] = [
  { value: 'audit-hms', labelKey: 'hmsAudit.modalTitle', deviceType: 'controller', fileSlug: 'hms-audit-report', Modal: HmsAuditReport },
  { value: 'audit-eht-insulation', labelKey: 'ehtInsulationAudit.modalTitle', deviceType: 'controller', fileSlug: 'eht-insulation-audit-report', Modal: EhtInsulationAuditReport },
  { value: 'audit-panel', labelKey: 'panelAudit.modalTitle', deviceType: 'controller', fileSlug: 'panel-audit-report', Modal: PanelAuditReport },
  { value: 'audit-eht-circuit', labelKey: 'ehtCircuitAudit.modalTitle', deviceType: 'circuit', fileSlug: 'eht-circuit-audit-report', Modal: EhtCircuitAuditReport },
];

const ALL_TYPES: FieldReportTypeDef[] = [...COMMISSIONING_TYPES, ...AUDIT_TYPES];

export const FIELD_REPORT_FILE_SLUG_MAP: Record<string, string> = {
  ...Object.fromEntries(ALL_TYPES.map(({ value, fileSlug }) => [value, fileSlug])),
  // Absent from the arrays above so it stays off both list screens, but still downloadable
  // from device pages.
  'commissioning-comm-loop': 'comm-loop-commissioning-report',
};

export const FIELD_REPORT_DEVICE_TYPE_MAP: Record<string, string> = Object.fromEntries(
  ALL_TYPES.map(({ value, deviceType }) => [value, deviceType])
);

export const FIELD_REPORT_TYPE_LABEL_MAP: Record<string, string> = Object.fromEntries(
  ALL_TYPES.map(({ value, labelKey }) => [value, labelKey])
);

export const COMMISSIONING_STATUS_OPTIONS = [
  { value: 'pass', labelKey: 'common.pass' },
  { value: 'pass-with-notes', labelKey: 'common.passWithNotes' },
  { value: 'failed', labelKey: 'common.fail' },
];
